import { useController } from '@aha-app/mvc';
import WhiteboardController from '../../controllers/WhiteboardController';
import { useEffect, useState } from 'react';

/**
 * A hook that gives you all objects on the canvas that match the predicate. Use
 * it in components that need to know about all objects of a specific type across
 * the entire whiteboard.
 *
 * @param {function} options.predicate - Function that returns true if the object is of the desired type (pass a function straight from guards.ts)
 * @returns {T[]} Array of all objects on the canvas that match the predicate
 *
 * @example
 * const allFrames = useWhiteboardObjects({
 *   predicate: isFrame
 * });
 */
const useWhiteboardObjects = <T extends fabric.Object>(
  predicate: (obj: fabric.Object) => obj is T
): T[] => {
  const controller = useController(WhiteboardController);
  const [, setRenderKey] = useState(1);

  useEffect(() => {
    const onUpdate = () => {
      setRenderKey(prev => prev + 1);
    };

    const events = ['object:changed', 'object:removed'];
    events.forEach(event => controller.canvas.on(event, onUpdate));
    return () => {
      events.forEach(event => controller.canvas.off(event, onUpdate));
    };
  }, [controller.canvas]);

  return controller.canvas.getObjects().filter(predicate);
};

export default useWhiteboardObjects;
